/**
 * Tenant Aliases API Client
 * Handles requests to tenant alias endpoints
 */

import { z } from 'zod';
import { TenantAliasResponseSchema } from '@resida/api-types/tenant-aliases';

const API_BASE = '/landlord/api/manager';

// Derived from TenantAliasResponseSchema in @resida/api-types/tenant-aliases
export type TenantAlias = z.infer<typeof TenantAliasResponseSchema>;

export interface TenantAliasListResponse {
  aliases: TenantAlias[];
}

export interface CreateTenantAliasRequest {
  alias: string;
  leaseId: string;
}

export async function fetchTenantAliases(tenantId: string): Promise<TenantAliasListResponse> {
  const response = await fetch(`${API_BASE}/tenants/${tenantId}/aliases`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Failed to fetch tenant aliases: ${response.status}`);
  return response.json();
}

export async function createTenantAlias(
  tenantId: string,
  data: CreateTenantAliasRequest
): Promise<TenantAlias> {
  const response = await fetch(`${API_BASE}/tenants/${tenantId}/aliases`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(`Failed to create tenant alias: ${response.status}`);
  return response.json();
}

export async function deactivateTenantAlias(tenantId: string, aliasId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/tenants/${tenantId}/aliases/${aliasId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Failed to deactivate tenant alias: ${response.status}`);
}
